import { PageTitle, Table } from '@/theme';
import { Button, Popconfirm, Spin } from 'antd';
import { RedoOutlined, DeleteOutlined } from '@ant-design/icons';
import { useApi, useApiMutation, useNotification } from '@/lib/hook';
import { dateFormat } from '@/lib/utils';

const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const UserBackups = ({ userId }) => {
    const send = useNotification();

    const { data, isFetching, refetch } = useApi({
        route: { name: 'user.backups', query: { id: userId } },
        setting: {
            enabled: Boolean(userId),
            select: (resp) => resp.data,
        },
    });

    const restore = useApiMutation({
        onSuccess: (resp) => {
            const restoredFrom = resp?.data?.details?.restored_from;
            const projectName = resp?.data?.details?.project?.name;
            send({
                type: 'success',
                description: restoredFrom
                    ? `Restored "${projectName}" from ${restoredFrom}.`
                    : 'Project restored as a clone.',
            });
        },
        onError: (error) => {
            send({
                type: 'error',
                description: error?.response?.data?.message || 'Restore failed.',
            });
        },
    });

    const remove = useApiMutation({
        onSuccess: () => {
            send({ type: 'success', description: 'Backup deleted.' });
            refetch();
        },
    });

    const onRestore = (id) => {
        restore.mutate({ route: { name: 'backup.restore', query: { id } } });
    };

    const onDelete = (id) => {
        remove.mutate({ route: { name: 'backup.delete', query: { id } } });
    };

    const columns = [
        { title: 'Project', dataIndex: 'project_name', key: 'project_name' },
        {
            title: 'Backup date',
            dataIndex: 'backup_date',
            key: 'backup_date',
            width: 130,
        },
        {
            title: 'Size',
            dataIndex: 'file_size_bytes',
            key: 'file_size_bytes',
            width: 100,
            render: formatSize,
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (v) => (v ? dateFormat(v) : '—'),
        },
        {
            title: '',
            key: 'actions',
            width: 140,
            render: (_, row) => (
                <>
                    <Popconfirm
                        title={`Restore project as a clone from ${row.backup_date}? The original will not be touched.`}
                        okText="Restore"
                        cancelText="Cancel"
                        onConfirm={() => onRestore(row.id)}
                    >
                        <Button
                            type="text"
                            size="small"
                            icon={<RedoOutlined />}
                            loading={restore.isLoading}
                        />
                    </Popconfirm>
                    <Popconfirm
                        title={`Delete backup from ${row.backup_date}? This cannot be undone.`}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        cancelText="Cancel"
                        onConfirm={() => onDelete(row.id)}
                    >
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={remove.isLoading}
                        />
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <>
            <PageTitle title="Backups" middle />
            <Spin spinning={isFetching}>
                <Table
                    rowKey="id"
                    dataSource={data || []}
                    columns={columns}
                    pagination={false}
                    locale={{ emptyText: 'No backups yet.' }}
                />
            </Spin>
        </>
    );
};
